import React from 'react'
import { Link } from 'react-router-dom'
import { SparklesIcon, MapPinIcon, EnvelopeIcon } from '@heroicons/react/24/outline'

const Footer = () => {
  const currentYear = new Date().getFullYear()

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <div className="flex items-center space-x-3 mb-4">
              <img src="/images/logo.png" alt="Twiggle Oral Care logo" className="h-10 w-10 object-contain rounded" />
              <span className="text-lg font-bold">Twiggle Oral Care</span>
            </div>
            <p className="text-gray-400 text-sm mb-5 max-w-sm leading-relaxed">
              Eco-friendly bamboo toothbrushes for dental clinics, oral health programs, and sustainability-focused organizations across Canada.
            </p>
            <div className="space-y-2">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold mb-4">
                <SparklesIcon className="w-3.5 h-3.5" />
                Eco-friendly • BPA-Free • Biodegradable
              </span>
              <div className="flex items-center space-x-2 text-gray-300">
                <MapPinIcon className="h-4 w-4 text-primary-400 flex-shrink-0" />
                <span className="text-xs">Toronto, Ontario, Canada</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-300">
                <EnvelopeIcon className="h-4 w-4 text-primary-400 flex-shrink-0" />
                <a href="mailto:twiggleoralcare@gmail.com" className="text-xs hover:text-primary-400 transition-colors">
                  twiggleoralcare@gmail.com
                </a>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h3 className="text-sm font-bold mb-4 text-white">Quick Links</h3>
            <ul className="space-y-2.5">
              {[
                { label: 'Home', to: '/' },
                { label: 'Shop', to: '/shop' },
                { label: 'Custom Packaging', to: '/bulk-orders' },
                { label: 'About Twiggle', to: '/about' },
                { label: 'Contact', to: '/contact' },
              ].map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className="text-gray-400 hover:text-primary-400 transition-colors text-sm">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Products */}
          <div>
            <h3 className="text-sm font-bold mb-4 text-white">Products</h3>
            <ul className="space-y-2.5">
              {[
                { label: 'Twiggle Green – Medium Soft', to: '/shop/twiggle-green' },
                { label: 'Twiggle Charcoal – Ultrasoft', to: '/shop/twiggle-charcoal' },
                { label: 'Twiggle White – Ultra Soft', to: '/shop/twiggle-white' },
                { label: 'Twiggle Mini – Pink', to: '/shop/twiggle-mini-pink' },
                { label: 'Twiggle Mini – Blue', to: '/shop/twiggle-mini-blue' },
              ].map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className="text-gray-400 hover:text-primary-400 transition-colors text-xs">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Divider */}
        <div className="mt-10 pt-6 border-t border-gray-800">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <SparklesIcon className="h-4 w-4 text-primary-400" />
              <span className="text-xs text-gray-400">Committed to a sustainable future, one smile at a time</span>
            </div>
            <div className="text-xs text-gray-500">
              © {currentYear} Twiggle Oral Care. All rights reserved.
            </div>
          </div>

          {/* Tags + Social */}
          <div className="mt-4 flex flex-wrap gap-2 items-center">
            {['Eco-friendly', 'BPA-Free Bristles', 'Biodegradable', 'Professional Grade', 'Canada Shipping'].map(tag => (
              <span key={tag} className="px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-[11px] text-gray-300">
                {tag}
              </span>
            ))}
            <div className="ml-auto flex gap-2">
              <a
                href="https://www.instagram.com/twiggleoralcare"
                target="_blank"
                rel="noopener noreferrer"
                className="px-2.5 py-1 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 text-[11px] text-gray-300 transition-colors"
              >
                Instagram
              </a>
              <a
                href="https://www.linkedin.com/in/twiggle-oral-care-0ba57237b/"
                target="_blank"
                rel="noopener noreferrer"
                className="px-2.5 py-1 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 text-[11px] text-gray-300 transition-colors"
              >
                LinkedIn
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
